// eslint-disable-next-line import/no-extraneous-dependencies
import { SearchResponse } from '@algolia/client-search';
import algoliasearch from 'algoliasearch';
import invariant from 'invariant';
import queryString from 'query-string';

import { configValue } from 'config/appConfigUtils';
import { RichError } from 'utils/RichError';
import { postJson } from 'utils/http';
import { trackError } from 'utils/trackError';

import { AlgoliaApiGeneralError } from './AlgoliaApiGeneralError';
import {
  AlgoliaApiValidUntilError,
  isValidUntilError,
} from './AlgoliaApiValidUntilError';
import { QueryOptions } from './types/QueryOptions';

type Args = {
  config: Readonly<{ appId: string; apiKey: string }>;
  query: string;
  queryOptions: QueryOptions;
  analytics?: boolean;
  updateApiKey?: () => Promise<string>;
};

export function algoliaApiSearch<TResponse>({
  config: { appId, apiKey },
  query,
  queryOptions,
  analytics = true,
  updateApiKey,
}: Args) {
  if (configValue('algolia', 'mock')) {
    return postJson<SearchResponse<TResponse>>(
      `http://algoliamock:6577/${queryOptions.index}/search`,
      {
        query,
        filters: queryOptions.filters,
        attributesToHighlight: queryOptions.attributesToHighlight,
      },
      {
        headers: {
          'x-algolia-api-key': apiKey,
        },
      },
    );
  }
  invariant(appId, 'Search app id must be configured');
  invariant(apiKey, 'Search api key must be configured');

  const { index, ...options } = queryOptions;
  const params = {
    ...options,
    getRankingInfo: true,
    clickAnalytics: true,
    analytics,
  };

  let client = algoliasearch(appId, apiKey);
  let indexClient = client.initIndex(index);

  let request = indexClient.search<TResponse>(
    query ? query.trim() : '',
    params,
  );

  return request.catch((e) => {
    if (isValidUntilError(e)) {
      if (updateApiKey) {
        return updateApiKey().then((newApiKey) => {
          client = algoliasearch(appId, newApiKey);
          indexClient = client.initIndex(index);
          request = indexClient.search(query ? query.trim() : '', params);
          return request;
        });
      }

      throw new AlgoliaApiValidUntilError();
    }

    const requestInfo = {
      debugData: e.debugData,
      errorMessage: e.message,
      requestParams: queryString.stringify(params),
      statusCode: e.statusCode,
      response: e.response,
    };

    trackError(
      new RichError('Algolia POST request error.', {
        requestInfo: JSON.stringify(requestInfo),
      }),
    );

    throw new AlgoliaApiGeneralError(
      `Algolia Error: ${JSON.stringify(requestInfo)}`,
    );
  });
}
